import React, { useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Logo } from '../logos/Logo';
import { Menu, X } from 'lucide-react';
import './Navigation.css';

const navigation_items = [
  { name: 'Home', href: '/', type: 'route' },
  { name: 'Visuals', href: '#visuals', type: 'scroll' },
  { name: 'Live Demo', href: '#demo', type: 'scroll' },
  { name: 'Blog', href: '/blog', type: 'route' },
  // { name: 'About', href: '/about', type: 'route' },
  { name: 'Contact', href: '/contact', type: 'route' },
];

const handleNavClick = (href: string, type: string, navigate: (path: string) => void) => {
  if (type === 'route') {
    if (href === '/') {
      // For home page, scroll to top if already on home, otherwise navigate
      if (window.location.pathname === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(href);
      }
    } else {
      navigate(href);
    }
  } else if (type === 'scroll') {
    // If we're not on the home page, first navigate to home
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation and then scroll
      setTimeout(() => {
        const element = document.getElementById(href.substring(1));
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const element = document.getElementById(href.substring(1));
      element?.scrollIntoView({ behavior: 'smooth' });
    }
  }
};

export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <motion.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="site-nav"
    >
      <div className="site-nav-inner">
        <button className="site-nav-logo" type="button" onClick={() => navigate('/')} aria-label="Go to home page">
          <Logo />
        </button>

        <div className="site-nav-links">
          {navigation_items.map((item, index) => {
            const isActive = (item.type === 'route' && location.pathname === item.href) ||
                            (item.type === 'scroll' && location.pathname === '/' && item.href.startsWith('#'));

            return (
              <motion.button
                key={item.name}
                type="button"
                onClick={() => handleNavClick(item.href, item.type, navigate)}
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.08 + index * 0.06 }}
                className={`site-nav-link ${isActive ? 'site-nav-link-active' : ''}`}
              >
                {item.name}
              </motion.button>
            );
          })}
        </div>

        <button
          className="site-nav-menu-button"
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {isOpen && (
        <div className="site-nav-mobile">
          {navigation_items.map((item) => (
            <button
              key={item.name}
              type="button"
              onClick={() => {
                handleNavClick(item.href, item.type, navigate);
                setIsOpen(false);
              }}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}
    </motion.nav>
  );
}
